import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '../../../Typography';
import { CELL_PAD_X, DATE_SAMPLES, EDGE_PAD, HEADER_FLOOR_CAP, HEADER_LABEL_GAP, SORT_GLYPH_WIDTH } from '../table.constants';
import styles from '../table.module.scss';
const probesFor = (columns)=>columns.map((column, index)=>{
        var _column_format;
        return {
            id: String(column.name),
            label: column.header,
            sortable: column.sortable === true,
            sample: column.type === 'date' ? DATE_SAMPLES[(_column_format = column.format) !== null && _column_format !== void 0 ? _column_format : 'date'] : undefined,
            startPad: index === 0 ? EDGE_PAD : CELL_PAD_X
        };
    });
const sameFloors = (a, b)=>{
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key)=>a[key] === b[key]);
};
// WHY: probes depend on labels and types only, so measuring cannot feed back into the widths.
export function useColumnFloors(param) {
    let { mainColumn, columns, trailingColumn, headerLabelVariant = 'paragraph3' } = param;
    const probes = useMemo(()=>probesFor([
            ...mainColumn == null ? [] : [
                mainColumn
            ],
            ...columns,
            ...trailingColumn == null ? [] : [
                trailingColumn
            ]
        ]), [
        mainColumn,
        columns,
        trailingColumn
    ]);
    const hostRef = useRef(null);
    const [floors, setFloors] = useState({});
    useLayoutEffect(()=>{
        var _document_fonts;
        const host = hostRef.current;
        if (host == null) return undefined;
        const measure = ()=>{
            const next = {};
            probes.forEach((probe)=>{
                const label = host.querySelector('[data-floor-label="'.concat(probe.id, '"]'));
                const sample = host.querySelector('[data-floor-sample="'.concat(probe.id, '"]'));
                const labelWidth = label == null ? 0 : label.scrollWidth;
                const sampleWidth = sample == null ? 0 : sample.scrollWidth;
                if (labelWidth === 0 && sampleWidth === 0) return;
                const cellChrome = probe.startPad + CELL_PAD_X;
                const headerChrome = cellChrome + (probe.sortable ? SORT_GLYPH_WIDTH + HEADER_LABEL_GAP : 0);
                const headerFloor = labelWidth === 0 ? 0 : Math.min(labelWidth + headerChrome, HEADER_FLOOR_CAP);
                const sampleFloor = sampleWidth === 0 ? 0 : sampleWidth + cellChrome;
                next[probe.id] = Math.ceil(Math.max(headerFloor, sampleFloor));
            });
            setFloors((current)=>sameFloors(current, next) ? current : next);
        };
        measure();
        // WHY: the first paint can run on a fallback font.
        let cancelled = false;
        (_document_fonts = document.fonts) === null || _document_fonts === void 0 ? void 0 : _document_fonts.ready.then(()=>!cancelled && measure());
        return ()=>{
            cancelled = true;
        };
    }, [
        probes
    ]);
    const measurer = /*#__PURE__*/ _jsx("div", {
        ref: hostRef,
        "aria-hidden": true,
        className: styles.floorMeasurer,
        children: probes.map((probe)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        variant: headerLabelVariant,
                        "data-floor-label": probe.id,
                        children: probe.label
                    }),
                    probe.sample != null && /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        "data-floor-sample": probe.id,
                        children: probe.sample
                    })
                ]
            }, probe.id))
    });
    return {
        floors,
        measurer
    };
}
